import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Mail, User, AtSign } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    tradingViewId: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    const result = await register(
      formData.email,
      formData.password,
      formData.tradingViewId,
      formData.fullName
    );

    if (result.success) {
      navigate('/account', { replace: true });
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Header */}
      <nav className="border-b border-neutral-800 bg-[#111315]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/logo.png" alt="HDAlgos logo" className="h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>
            <Link to="/" className="text-sm text-neutral-400 hover:text-white transition flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
          <h1 className="text-3xl font-black mb-2">Create Account</h1>
          <p className="text-neutral-400 mb-8">Get started with HDAlgos</p>

          {error && (
            <div className="bg-red-950/30 ring-1 ring-red-900/50 rounded-lg p-4 mb-6">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Full Name */}
            <div className="mb-6">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="text"
                  name="fullName"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>
            </div>

            {/* Email */}
            <div className="mb-6">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>
            </div>

            {/* TradingView ID */}
            <div className="mb-6">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                TradingView Username (Optional)
              </label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="text"
                  name="tradingViewId"
                  placeholder="@yourusername"
                  value={formData.tradingViewId}
                  onChange={handleChange}
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>
              <p className="text-xs text-neutral-500 mt-2">
                You can add this later if you don't have one yet
              </p>
            </div>

            {/* Password */}
            <div className="mb-6">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="password"
                  name="password"
                  placeholder="At least 6 characters"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div className="mb-6">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg pl-11 pr-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-4 rounded-lg font-black text-lg hover:from-[#D8C585] hover:to-[#BDA45E] transition transform hover:scale-[1.02] mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            {/* Login Link */}
            <p className="text-center text-sm text-neutral-400">
              Already have an account?{' '}
              <Link to="/login" className="text-[#CBB26A] hover:text-[#D8C585] font-bold">
                Sign In
              </Link>
            </p>

            {/* Terms */}
            <p className="text-xs text-neutral-600 text-center mt-4">
              By creating an account, you agree to our Terms of Service and Privacy Policy
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
